import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Search, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { Badge } from "~/components/ui/badge";
import { Button, ButtonLink } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/cn";
import {
  adminStoriesQueryOptions,
  type AdminStoryListInput,
  type AdminStoryRow,
} from "~/lib/functions/transformation-story";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import { NOVA_SUCCESS_STORY_SOURCE } from "~/lib/shared/transformation-story";

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { value: "in_review", label: "In Review" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]["value"];

const storiesSearchSchema = z.object({
  status: z.enum(["in_review", "published", "archived", "all"]).optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).optional(),
});

const STATUS_BY_FILTER: Record<StatusFilter, ContentStatus | null> = {
  in_review: ContentStatus.InReview,
  published: ContentStatus.Published,
  archived: ContentStatus.Archived,
  all: null,
};

export const Route = createFileRoute("/admin/cih/stories/")({
  staticData: { title: "Success Stories" },
  validateSearch: storiesSearchSchema,
  loaderDeps: ({ search }) => ({
    status: search.status ?? "in_review",
    search: search.q ?? "",
    page: search.page ?? 1,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      adminStoriesQueryOptions(toListInput(deps)),
    );
  },
  component: SuccessStoriesPage,
});

function toListInput(deps: {
  status: StatusFilter;
  search: string;
  page: number;
}): AdminStoryListInput {
  return {
    page: deps.page,
    pageSize: PAGE_SIZE,
    search: deps.search,
    status: STATUS_BY_FILTER[deps.status],
  };
}

function countForFilter(
  filter: StatusFilter,
  counts: Record<ContentStatus, number>,
): number {
  const status = STATUS_BY_FILTER[filter];
  return status === null
    ? Object.values(counts).reduce((sum, count) => sum + count, 0)
    : counts[status];
}

function SuccessStoriesPage(): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath });
  const { status, q, page } = Route.useSearch();
  const statusFilter = status ?? "in_review";
  const search = q ?? "";
  const currentPage = page ?? 1;

  const [searchInput, setSearchInput] = useState(search);
  // The search this page last wrote to the URL. A slow loader can resolve that
  // older value while the admin keeps typing; syncing it back into the input
  // would drop the newer characters. Only a search that came from elsewhere
  // (back/forward, a tab change) replaces the input.
  const sentSearchRef = useRef(search);

  useEffect(() => {
    if (search === sentSearchRef.current) return;
    sentSearchRef.current = search;
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchInput === search) return;

    const timer = setTimeout(() => {
      sentSearchRef.current = searchInput;
      navigate({
        search: (prev) => ({
          ...prev,
          q: searchInput === "" ? undefined : searchInput,
          page: undefined,
        }),
        replace: true,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, search, navigate]);

  const { data, isFetching, isPlaceholderData } = useQuery(
    adminStoriesQueryOptions(
      toListInput({ status: statusFilter, search, page: currentPage }),
    ),
  );

  const rows = data?.rows ?? [];
  const totalRows = data?.totalRows ?? 0;
  const pageCount = Math.max(1, data?.pageCount ?? 1);

  // Publishing or archiving from the review page shrinks the list, so the
  // page an admin returns to can be past the end.
  useEffect(() => {
    if (!data || isPlaceholderData || currentPage <= pageCount) return;
    navigate({
      search: (prev) => ({ ...prev, page: pageCount }),
      replace: true,
    });
  }, [data, isPlaceholderData, currentPage, pageCount, navigate]);

  const goToPage = (nextPage: number): void => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage === 1 ? undefined : nextPage,
      }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium text-gray-900">
            Success Stories
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Client outcomes that coaches submit through Nova, plus imported
            summit stories. A submission appears on the public stories page only
            after you publish it.
          </p>
        </div>
        <ButtonLink
          href="/stories"
          target="_blank"
          rel="noopener noreferrer"
          variant="default"
          size="sm"
          className="gap-1.5"
        >
          Public stories page
          <ExternalLink />
        </ButtonLink>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              variant={statusFilter === filter.value ? "primary" : "ghost"}
              size="sm"
              onClick={() =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    status:
                      filter.value === "in_review" ? undefined : filter.value,
                    page: undefined,
                  }),
                })
              }
            >
              {filter.label}
              {data ? (
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-xs leading-none tabular-nums",
                    statusFilter === filter.value
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600",
                  )}
                >
                  {countForFilter(filter.value, data.statusCounts)}
                </span>
              ) : null}
            </Button>
          ))}
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchInput}
            onValueChange={setSearchInput}
            placeholder="Search client, coach, or challenge"
            aria-label="Search stories"
            className="mb-0 pl-9"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border bg-white shadow-sm">
          <StoriesEmptyState statusFilter={statusFilter} search={search} />
        </div>
      ) : (
        <div
          className={cn(
            "transition-opacity",
            isFetching && isPlaceholderData && "opacity-60",
          )}
        >
          <Table className="bg-white">
            <TableHeader>
              <TableRow>
                <TableHead>Story</TableHead>
                <TableHead>Coach</TableHead>
                <TableHead>Challenge</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((story) => (
                <StoryRow key={story.id} story={story} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalRows > 0 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-500">
            {totalRows} {totalRows === 1 ? "story" : "stories"}
            {pageCount > 1 ? ` · Page ${currentPage} of ${pageCount}` : ""}
          </span>
          {pageCount > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={currentPage >= pageCount}
                onClick={() => goToPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StoryRow({ story }: { story: AdminStoryRow }): React.ReactElement {
  const isNova = story.source === NOVA_SUCCESS_STORY_SOURCE;

  return (
    <TableRow>
      <TableCell className="max-w-sm min-w-56">
        <Link
          to="/admin/cih/stories/$storyId"
          params={{ storyId: story.id }}
          className="font-medium text-gray-900 hover:text-brand-600 hover:underline"
        >
          {story.name}
        </Link>
        <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
          {story.body}
        </p>
      </TableCell>
      <TableCell className="text-gray-700">
        {story.coachFullName ?? story.coachUsername ?? (
          <span className="text-gray-400">—</span>
        )}
      </TableCell>
      <TableCell className="text-gray-700">
        {story.challengeTitle ?? <span className="text-gray-400">—</span>}
      </TableCell>
      <TableCell>
        {isNova ? (
          <Badge variant="outline-primary" size="sm">
            <Sparkles className="h-3 w-3" />
            Nova
          </Badge>
        ) : (
          <Badge variant="gray" size="sm">
            {story.source}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <ContentStatusBadge status={story.status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-gray-500">
        {new Date(story.submittedAt ?? story.createdAt).toLocaleDateString(
          "en-US",
          { month: "short", day: "numeric", year: "numeric" },
        )}
      </TableCell>
    </TableRow>
  );
}

function StoriesEmptyState({
  statusFilter,
  search,
}: {
  statusFilter: StatusFilter;
  search: string;
}): React.ReactElement {
  if (search !== "") {
    return (
      <EmptyState
        size="sm"
        icon={Search}
        title="No matches"
        description={`No story in this view matches "${search}".`}
      />
    );
  }

  if (statusFilter === "in_review") {
    return (
      <EmptyState
        size="sm"
        icon={Sparkles}
        title="Nothing to review"
        description="New Nova submissions appear here."
      />
    );
  }

  return (
    <EmptyState
      size="sm"
      icon={Sparkles}
      title="No stories in this view"
      description="Try another status."
    />
  );
}
